import router from '@/router';
import { haveProperty } from '@/utils/';

export default {
    model: () => {
        // obtenção da rota
        const route = router.currentRoute;
        const { model } = route.meta;
        const apiModel = route.meta.api;
        const isEdit = !haveProperty( route.meta, 'isAdd' );
        const customEndpoint = route.meta.associationAPI && route.path.substr( 0, route.path.lastIndexOf( '/' ) );

        /**
         * [função]
         * Preparação dos valores
         * default para a submissão
         * dos campos para a API.
         *
         * @since 1.0.0
         * @category function
         * @author Tiago Martinho
         *
         */
        const prepare = () => ({
            model,
            isEdit,
            apiModel,
            ...( isEdit && { modelId: route.params.id }),
            ...( customEndpoint && { customEndpoint })
        });

        /**
         * [funcão]
         * Comportamento que ocorrerá depois de
         * uma submissão default para a API com o
         * estado de sucesso.
         *
         * @param {Function} commit
         * @param {Object} response
         * @since 1.0.0
         * @category function
         * @author Tiago Martinho
         *
         */
        const successAction = ({ commit, payload: { response, newModel, customRoute } }) => {
            const routePrefix = customRoute || `/${apiModel}`;

            ( isEdit )
                ? commit( 'UPDATE_MODEL' )
                : router.push( `${routePrefix}/${response.content.id}` );

            // caso o modelo seja campanha,
            // é necessário actualizar os helpers e fechar
            // os modais abertos caso seja necessário.
            if ( model === 'campaign' ) {
                commit( 'SAVE_HELPER', { selectedUsers: response.content.users.map( ({ id }) => id ) });
                commit( 'TOGGLE_TRIGGER', true );

                // Se for um modal de remoção de estado de chamada
                if ( newModel.contactCallStateId ) {
                    // definação dos novos parameters.
                    const params = { type: 'callRule' };
                    commit( 'UPDATE_BLOCKS', params );
                }
            }
        };

        return {
            prepare,
            successAction
        };
    },
    token: () => {
        const route = router.currentRoute;
        const model = 'token';
        // eslint-disable-next-line
        let isNewToken =  true;

        /**
         * [função]
         * Preparação dos valores
         * necessários para a submissão
         * dos campos de criação de token
         * para a API.
         *
         * @param {Object} token
         * @since 1.0.0
         * @category function
         * @author Tiago Martinho
         *
         */
        const prepare = token => {
            // se houver método no payload, significa
            // que é uma edição
            isNewToken = !token.method;

            return {
                model,
                apiModel: model,
                // caso seja uma edição
                ...( isNewToken
                    ? { helpers: { id: route.params.id } }
                    : { isEdit: true }
                )
            };
        };

        /**
         * [função]
         * Comportamento que ocorrerá depois de
         * uma submissão de gerar token para a API com o
         * estado de sucesso.
         *
         * @param {Function} commit
         * @param {Object} response
         * @param {Object} newModel
         * @since 1.0.0
         * @category função
         * @author Tiago Martinho
         *
         */
        const successAction = ({ commit, payload: { response, newModel } }) => {
            // validação do tipo de token.
            const type = newModel.isDelete
                ? 'delete'
                : isNewToken ? 'new' : 'update';

            // definação dos novos parameters.
            const params = {
                token: {
                    type,
                    data: type === 'new'
                        ? response.content
                        : {
                            id: newModel.id,
                            ...( newModel.revoked && { revoked: newModel.revoked })
                        }

                }
            };

            commit( 'UPDATE_BLOCKS', params );

            if ( type !== 'new' )
                commit( 'TOGGLE_TRIGGER', true );
        };

        return {
            prepare,
            successAction
        };
    },
    script: () => {
        const route = router.currentRoute;

        /**
         * [função]
         * Preparação dos valores
         * necessários para a submissão
         * dos campos de activação / apagar
         * o script.
         *
         * @param {Object} script
         * @since 1.0.0
         * @category function
         * @author Tiago Martinho
         *
         */
        const prepare = () => ({
            model: 'script',
            customEndpoint: `/campaigns/${route.params.id}/scripts`,
            isEdit: true
        });

        /**
         * [função]
         * Comportamento que ocorrerá depois de
         * uma submissão de edição / apagar script
         * estado de sucesso.
         *
         * @param {Function} commit
         * @param {Object} newModel
         * @since 1.0.0
         * @category função
         * @author Tiago Martinho
         *
         */
        const successAction = ({ commit, payload: { newModel } }) => {
            // definação dos novos parameters.
            const params = {
                script: {
                    type: newModel.isDelete ? 'delete' : 'update',
                    data: {
                        id: newModel.id
                    }

                }
            };

            commit( 'UPDATE_BLOCKS', params );
        };

        return {
            prepare,
            successAction
        };
    },
    call: () => {
        /**
         * [função]
         * Preparação dos valores
         * necessários para a submissão
         * dos campos de criação de chamada
         * para a API.
         *
         * @param {Object} call
         * @since 1.0.0
         * @category função
         * @author Tiago Martinho
         *
         */
        const prepare = call => ({
            model: 'call',
            customEndpoint: `/contacts/${call.requestId}/calls`,
            successAction: 'callSuccessAction'
        });

        /**
         * [função]
         * Comportamento que ocorrerá depois de
         * uma submissão de uma nova chamada
         *
         * @param {function} commit
         * @since 1.0.0
         * @category função
         * @author Tiago Martinho
         *
         */
        const successAction = ({ commit }) => {
            commit( 'TOGGLE_TRIGGER', true );
        };

        return {
            prepare,
            successAction
        };
    },
    associateRecordings: () => {
        const route = router.currentRoute;
        /**
         * [função]
         * Preparação dos valores
         * necessários para a submissão
         * dos campos de associação de gravação
         * para a API.
         *
         * @param {Object} associateRecordings
         * @since 1.0.0
         * @category função
         * @author Pedro Fragata
         *
         */
        const prepare = object => ({
            model: 'associateRecordings',
            customEndpoint: `contacts/${route.params.id}/calls/${object.call}/audio`,
            successAction: 'callSuccessAction'
        });

        /**
         * [função]
         * Comportamento que ocorrerá depois de
         * uma submissão de uma nova chamada
         *
         * @param {function} commit
         * @since 1.0.0
         * @category função
         * @author Tiago Martinho
         *
         */
        const successAction = ({ commit }) => {
            commit( 'UPDATE_MODEL' );
            commit( 'TOGGLE_TRIGGER', true );
        };

        return {
            prepare,
            successAction
        };
    },
    resolve: () => {
        /**
         * [função]
         * Preparação dos valores
         * necessários para a submissão
         * dos campos de criação de chamada
         * para a API.
         *
         * @param {Object} resolve
         * @since 1.0.0
         * @category função
         * @author Pedro Fragata
         *
         */
        const prepare = () => ({
            model: 'resolveCall',
            customEndpoint: '/contacts/resolve',
            successAction: 'callSuccessAction'
        });

        /**
         * [função]
         * Comportamento que ocorrerá depois de
         * uma submissão de uma nova chamada
         *
         * @param {function} commit
         * @since 1.0.0
         * @category função
         * @author Tiago Martinho
         *
         */
        const successAction = ({ commit, payload: { response, newModel } }) => {
            // validação do tipo de chamada.
            const type = newModel.isDelete
                ? 'delete'
                : 'new';

            // definação dos novos parameters.
            const params = {
                call: {
                    type,
                    data: type === 'new'
                        ? response.content.contacts.calls
                        : {
                            id: newModel.id,
                            ...( newModel.revoked && { revoked: newModel.revoked })
                        }

                }
            };

            commit( 'UPDATE_BLOCKS', params );
            commit( 'TOGGLE_TRIGGER', true );
        };

        return {
            prepare,
            successAction
        };
    },
    editCall: () => {
        /**
         * [função]
         * Preparação dos valores
         * necessários para a submissão
         * dos campos de criação de chamada
         * para a API.
         *
         * @param {Object} editCall
         * @since 1.0.0
         * @category função
         * @author Tiago Martinho
         *
         */
        const prepare = call => ({
            model: 'editCall',
            customEndpoint: `/contacts/${call.requestId}/calls`,
            successAction: 'callSuccessAction',
            isEdit: true,
            modelId: call.requestId
        });

        /**
         * [função]
         * Comportamento que ocorrerá depois de
         * edição de uma chamada
         *
         * @param {function} commit
         * @since 1.0.0
         * @category função
         * @author Tiago Martinho
         *
         */
        /* const successAction = ({ commit }) => {
            commit( 'TOGGLE_TRIGGER', true );
        }; */

        const successAction = ({ commit, payload: { response } }) => {
            // definação dos novos parameters.
            const params = {
                call: {
                    type: 'edit',
                    data: response.content
                }
            };

            commit( 'UPDATE_BLOCKS', params );
            commit( 'TOGGLE_TRIGGER', true );
        };

        return {
            prepare,
            successAction
        };
    },
    importContacts: () => {
        const route = router.currentRoute;
        /**
         * [função]
         * Preparação dos valores
         * necessários para a submissão
         * dos campos de criação de chamada
         * para a API.
         *
         * @param {Object} importContact
         * @since 1.0.0
         * @category função
         * @author Pedro Fragata
         *
         */
        const prepare = () => ({
            model: 'importContacts',
            customEndpoint: `/campaigns/${route.params.id}/import-contact-requests`
        });

        /**
         * [função]
         * Comportamento que ocorrerá depois
         * da importação de contactos
         * @param {function} commit
         * @since 1.0.0
         * @category função
         *
         *
         */
        const successAction = ({ commit }) => {
            commit( 'TOGGLE_TRIGGER', true );
        };

        return {
            prepare,
            successAction
        };
    },
    callRules: () => {
        const route = router.currentRoute;
        /**
         * [função]
         * Preparação dos valores
         * necessários para a submissão
         * dos campos de criação de uma
         * regra de chamada.
         *
         * @since 1.2.8
         * @category função
         * @author Jorge Monteiro
         *
         */
        const prepare = () => ({
            model: 'campaign',
            customEndpoint: '/campaigns',
            isEdit: !haveProperty( route.meta, 'isAdd' ),
            modelId: route.params.id
        });

        /**
         * [função]
         * Comportamento que ocorrerá depois
         * da criação da regra de chamada
         * @param {function} commit
         * @since 1.2.8
         * @category função
         *
         *
         */
        const successAction = ({ commit, payload: { response } }) => {
            // definação dos novos parameters.
            const params = {
                type: 'callRule',
                rule: {
                    data: response.content
                }
            };

            commit( 'UPDATE_BLOCKS', params );
            commit( 'UPDATE_MODEL' );
        };

        return {
            prepare,
            successAction
        };
    },
    updateContacts: () => {
        // obtenção da rota
        const route = router.currentRoute;

        /**
         * [função]
         * Preparação dos valores para a
         * submissão dos campos para a API.
         *
         * @since 1.2.3
         * @category function
         * @author Jorge Monteiro
         *
         */
        const prepare = () => ({
            model: 'contact',
            isEdit: true,
            modelId: route.params.id,
            customEndpoint: '/contacts'
        });

        /**
         * [funcão]
         * Comportamento que ocorrerá depois de
         * uma submissão de uma alteração de contacto
         * para a API com o estado de sucesso.
         *
         * @param {Function} commit
         * @since 1.2.3
         * @category function
         * @author Jorge Monteiro
         *
         */
        const successAction = ({ commit }) => {
            commit( 'UPDATE_MODEL' );
            commit( 'TOGGLE_TRIGGER', true );
        };

        return {
            prepare,
            successAction
        };
    },
    sendEmail: () => {
        /**
         * [função]
         * Preparação dos valores
         * necessários para a submissão
         * dos campos de envio de e-mail
         * para a API.
         *
         * @param {Object} call
         * @since 1.2.3
         * @category função
         * @author Jorge Monteiro
         *
         */
        const prepare = sendEmail => ({
            model: 'sendEmail',
            customEndpoint: `/contacts/${sendEmail.requestId}/mail`,
            successAction: 'callSuccessAction'
        });

        /**
         * [função]
         * Comportamento que ocorrerá depois de
         * uma submissão de envio de um email.
         *
         * @param {function} commit
         * @since 1.2.3
         * @category função
         * @author Jorge Monteiro
         *
         */
        const successAction = ({ commit }) => {
            commit( 'TOGGLE_TRIGGER', true );
        };

        return {
            prepare,
            successAction
        };
    },
    duplicateScript: () => {
        const route = router.currentRoute;
        /**
         * [função]
         * Preparação dos valores
         * necessários para a submissão
         * de uma duplicação de guião
         * para a API.
         *
         * @param {Object} associateRecordings
         * @since 1.0.0
         * @category função
         * @author Pedro Fragata
         *
         */
        const prepare = () => ({
            model: 'duplicateScript',
            customEndpoint: `${route.path}/replicate`,
            successAction: 'callSuccessAction'
        });

        /**
         * [função]
         * Comportamento que ocorrerá depois de
         * uma submissão de uma duplicação de guião
         *
         * @param {function} commit
         * @since 1.0.0
         * @category função
         * @author Tiago Martinho
         *
         */
        const successAction = ({ commit, payload: { response, newModel } }) => {
            commit( 'UPDATE_MODEL' );
            commit( 'CONTROL_ACTION_BAR', { type: 'block', value: false });
            router.push( `/campaigns/${newModel.campaign}/scripts/${response.content.id}` );
        };

        return {
            prepare,
            successAction
        };
    }
};